<template>
  <div
    class="relative flex items-center gap-1 rounded bg-white p-1"
    :class="fileClass"
    data-cy="uploaded-file-item"
    @click="openPreview(file)"
  >
    <icon-base :icon="docIcon" class="shrink-0 mb-0!" />
    <span
      class="text-sm truncate text-headline-new"
      v-tooltipable.collapsed="formattedSize"
    >
      {{ file.name }}
    </span>
    <button
      class="absolute -top-2 -right-2"
      data-cy="remove-file-button"
      @click.stop="emit('file:remove')"
    >
      <icon-base :icon="IconRemoveFile" />
    </button>
  </div>
  <document-preview-modal
    v-if="showPreviewModal && previewItem"
    :item="previewItem"
    :close="closePreview"
  />
</template>

<script setup lang="ts">
import { computed } from "vue";

import { storeToRefs } from "pinia";

import { getFileIcon } from "@/helpers/files";
import { formatFileSize } from "@/helpers/formatting";
import { useFilePreview } from "@/hooks/useFilePreview";
import { useFiles } from "@/stores/ai/files";

import DocumentPreviewModal from "@/components/deals/documents/DocumentPreviewModal.vue";
import IconRemoveFile from "@/components/icons/IconRemoveFile.vue";
import IconBase from "@/components/ui/IconBase.vue";

const emit = defineEmits<{ "file:remove": [] }>();

const { file } = defineProps<{ file: File }>();

const aiFilesStore = useFiles();
const { invalidFiles, failedFiles } = storeToRefs(aiFilesStore);

const { showPreviewModal, previewItem, openPreview, closePreview } =
  useFilePreview();

const formattedSize = computed(() => {
  const bytesToKb = Math.round(file.size / 1024);
  const { unit, size } = formatFileSize(bytesToKb);
  return `${size}${unit}`;
});

const fileClass = computed(() =>
  failedFiles.value.has(file) || invalidFiles.value.has(file)
    ? "border border-red-500"
    : "border border-divider hover:border-silver hover:bg-ghost-white hover:cursor-pointer"
);

const docIcon = computed(() => getFileIcon(file.name));
</script>
